/**
 * WindowFramePicker — the frame sub-editor of `WindowSpecEditor`: a unit
 * dropdown (ROWS / RANGE / GROUPS) plus start- and end-bound dropdowns (the
 * classic "dropdown beats free-typing" case ANA-05/07/09/16 calls out —
 * UNBOUNDED PRECEDING / N PRECEDING / CURRENT ROW / N FOLLOWING / UNBOUNDED
 * FOLLOWING, with an inline number input for the N kinds). A `[+ add frame]`
 * seeds a default frame; a `× remove frame` clears it.
 *
 * Truthful capability note (NOT a hard block): GROUPS frames aren't supported
 * on every engine, and RANGE numeric-offset frames need exactly one ORDER BY
 * key and aren't available on SQL Server. The engine is known at edit time, so
 * these are stated as plain facts (`supportsGroupsFrame` / `supportsRangeOffsetFrame`
 * decide support explicitly) rather than as an unverifiable server-version hedge.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import { ENGINE_DISPLAY_NAME } from '../../dialect/statementCapabilities';
import { vqeCopy } from '../../ux/vqeCopy';
import { AddButton } from '../common/AddButton';
import { RemoveButton } from '../common/RemoveButton';
import { BoundDropdown } from './BoundDropdown';
import type { FrameBound, FrameBoundKind, FrameSpec, FrameUnit } from './overBodySpec';
import {
  clampFrameOffset,
  demoteNumericBound,
  endOrImplicitCurrentRow,
  numericOffsetsAllowed,
  reachableEndKinds,
  reachableImplicitEnd,
  reachableStartKinds,
  supportsGroupsFrame,
  withoutNumericOffsets,
} from './windowFrameOptions';

export interface WindowFramePickerProps {
  readonly frame: FrameSpec | null;
  readonly onChange: (next: FrameSpec | null) => void;
  readonly testIdPrefix: string;
  /** Active engine — GROUPS/RANGE-offset support is engine-known, so a frame the
   *  engine can't run gets a truthful note (I4 / Lane C-2). */
  readonly engine: SqlEngine;
  /** Number of ORDER BY keys on the window. RANGE numeric offsets need EXACTLY
   *  one (PG 42P20; others reject 0 or ≥2); GROUPS offsets need ≥1 — so the gate
   *  threads the COUNT, not a boolean, to keep a valid frame from becoming
   *  engine-invalid SQL (Lane C-2). */
  readonly orderByKeyCount: number;
}

const UNIT_OPTIONS: readonly FrameUnit[] = ['rows', 'range', 'groups'];
const START_KINDS: readonly FrameBoundKind[] = [
  'unbounded-preceding',
  'n-preceding',
  'current-row',
  'n-following',
];
const END_KINDS: readonly FrameBoundKind[] = [
  'n-preceding',
  'current-row',
  'n-following',
  'unbounded-following',
];

const BOUND_LABEL: Record<FrameBoundKind, string> = {
  'unbounded-preceding': 'UNBOUNDED PRECEDING',
  'n-preceding': 'N PRECEDING',
  'current-row': 'CURRENT ROW',
  'n-following': 'N FOLLOWING',
  'unbounded-following': 'UNBOUNDED FOLLOWING',
};

const DEFAULT_FRAME: FrameSpec = {
  unit: 'rows',
  start: { kind: 'unbounded-preceding' },
  end: { kind: 'current-row' },
};

export function WindowFramePicker({ frame, onChange, testIdPrefix, engine, orderByKeyCount }: WindowFramePickerProps) {
  const prefix = `${testIdPrefix}-frame`;

  if (!frame) {
    return (
      <div className="flex items-center gap-2" data-testid={`${prefix}-row`}>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Frame</span>
        <span className="text-[11px] italic text-muted-foreground" data-testid={`${prefix}-empty`}>
          {vqeCopy.window.noFrame}
        </span>
        <AddButton
          testId={`${prefix}-add`}
          label={vqeCopy.window.addFrame}
          disabled={false}
          onClick={() => onChange(DEFAULT_FRAME)}
        />
      </div>
    );
  }

  // RANGE/GROUPS numeric offsets are gated on the ORDER-BY key COUNT and engine
  // (I4 / Lane C-2): RANGE needs EXACTLY one key and isn't supported on mssql;
  // GROUPS needs ≥1. When the gate isn't met, numeric-offset bound kinds are
  // withheld, and switching a unit demotes any existing numeric bound — so a
  // valid frame can never be turned into engine-invalid SQL from the picker.
  const allowNumeric = numericOffsetsAllowed(frame.unit, orderByKeyCount, engine);
  const groupsUnsupportedHere = frame.unit === 'groups' && !supportsGroupsFrame(engine);
  // The "add a sort" note fits only the genuinely fixable case: a RANGE/GROUPS
  // frame with NO ORDER BY. The multi-key and mssql RANGE cases (where adding a
  // sort wouldn't help) are covered by the always-on capability note instead, so
  // this note never makes a false "add a sort to enable it" promise.
  const frameNeedsOrderBy = frame.unit !== 'rows' && orderByKeyCount === 0;

  const setUnit = (unit: FrameUnit): void => {
    if (!numericOffsetsAllowed(unit, orderByKeyCount, engine)) {
      onChange({
        unit,
        start: demoteNumericBound(frame.start, 'start'),
        end: frame.end ? demoteNumericBound(frame.end, 'end') : frame.end,
      });
      return;
    }
    onChange({ ...frame, unit });
  };
  // The start bound is never implicit — the START_KINDS control has no
  // `allowImplicit`, so it never emits null; guard anyway to satisfy the shared
  // `FrameBoundControl` onChange signature (the end bound DOES allow null).
  const setStart = (start: FrameBound | null): void => { if (start) onChange({ ...frame, start }); };
  const setEnd = (end: FrameBound | null): void => onChange({ ...frame, end });

  // Constrain each dropdown to the bound kinds that keep the frame ordering-
  // valid (end >= start on the `frameBoundRank` axis) against the OTHER
  // bound's current value — this is what makes an invalid frame unreachable
  // rather than merely rejected after the fact (Task §4b-i-fix). When numeric
  // offsets aren't allowed (RANGE/GROUPS with no ORDER BY), drop them too (I4).
  const startKinds = allowNumeric
    ? reachableStartKinds(START_KINDS, frame.end)
    : withoutNumericOffsets(reachableStartKinds(START_KINDS, frame.end));
  const endKinds = allowNumeric
    ? reachableEndKinds(END_KINDS, frame.start)
    : withoutNumericOffsets(reachableEndKinds(END_KINDS, frame.start));
  const endImplicitAllowed = reachableImplicitEnd(frame.start);

  return (
    <div className="flex flex-col gap-1.5" data-testid={`${prefix}-row`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Frame</span>
        <BoundDropdown
          testId={`${prefix}-unit`}
          label={frame.unit.toUpperCase()}
          ariaLabel={`Frame unit (currently ${frame.unit})`}
          options={UNIT_OPTIONS.map((u) => ({ key: u, label: u.toUpperCase(), active: u === frame.unit }))}
          onSelect={(k) => setUnit(k as FrameUnit)}
        />
        <span className="text-[10px] text-muted-foreground">from</span>
        <FrameBoundControl
          testId={`${prefix}-start`}
          bound={frame.start}
          kinds={startKinds}
          other={endOrImplicitCurrentRow(frame.end)}
          role="start"
          onChange={setStart}
        />
        <span className="text-[10px] text-muted-foreground">to</span>
        <FrameBoundControl
          testId={`${prefix}-end`}
          bound={frame.end}
          kinds={endKinds}
          allowImplicit={endImplicitAllowed}
          other={frame.start}
          role="end"
          onChange={setEnd}
        />
        <RemoveButton
          testId={`${prefix}-remove`}
          ariaLabel="Remove frame"
          disabled={false}
          tooltip={vqeCopy.window.removeFrame}
          onClick={() => onChange(null)}
        />
      </div>
      {frameNeedsOrderBy ? (
        <p className="text-[10px] italic text-muted-foreground" data-testid={`${prefix}-orderby-note`}>
          {vqeCopy.window.frameOffsetNeedsOrderBy}
        </p>
      ) : null}
      {groupsUnsupportedHere ? (
        <p className="text-[10px] italic text-muted-foreground" data-testid={`${prefix}-groups-note`}>
          {vqeCopy.window.frameGroupsUnsupported(ENGINE_DISPLAY_NAME[engine])}
        </p>
      ) : null}
      <p className="text-[10px] italic text-muted-foreground" data-testid={`${prefix}-capability`}>
        {vqeCopy.window.frameCapabilityNote}
      </p>
    </div>
  );
}

interface FrameBoundControlProps {
  readonly testId: string;
  readonly bound: FrameBound | null;
  readonly kinds: readonly FrameBoundKind[];
  /** The other (fixed) bound — used to keep a kind pick / typed offset
   *  ordering-valid against it (see `windowFrameOptions.ts`). */
  readonly other: FrameBound;
  readonly role: 'start' | 'end';
  /** The end bound may be omitted (single-bound frame → implicit CURRENT ROW). */
  readonly allowImplicit?: boolean;
  readonly onChange: (next: FrameBound | null) => void;
}

function FrameBoundControl({ testId, bound, kinds, other, role, allowImplicit, onChange }: FrameBoundControlProps) {
  const currentKind = bound?.kind ?? 'implicit';
  const options = [
    ...(allowImplicit ? [{ key: 'implicit', label: '(implicit CURRENT ROW)', active: currentKind === 'implicit' }] : []),
    ...kinds.map((k) => ({ key: k, label: BOUND_LABEL[k], active: k === currentKind })),
  ];
  const isN = bound?.kind === 'n-preceding' || bound?.kind === 'n-following';

  const pick = (key: string): void => {
    if (key === 'implicit') { onChange(null); return; }
    const kind = key as FrameBoundKind;
    if (kind === 'n-preceding' || kind === 'n-following') {
      const desired = bound?.kind === kind ? (bound.n ?? 1) : 1;
      onChange({ kind, n: clampFrameOffset(kind, desired, other, role) });
    } else {
      onChange({ kind });
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <BoundDropdown
        testId={testId}
        label={bound ? BOUND_LABEL[bound.kind] : '(implicit CURRENT ROW)'}
        ariaLabel={`Frame bound (currently ${bound?.kind ?? 'implicit'})`}
        options={options}
        onSelect={pick}
      />
      {isN ? (
        <input
          type="number"
          min={0}
          value={bound?.n ?? 1}
          aria-label="Frame offset"
          data-testid={`${testId}-n`}
          onChange={(e) => {
            const raw = Number(e.target.value);
            if (bound && (bound.kind === 'n-preceding' || bound.kind === 'n-following')) {
              onChange({ kind: bound.kind, n: clampFrameOffset(bound.kind, raw, other, role) });
            }
          }}
          className="h-6 w-14 rounded border border-input bg-background px-1 text-[11px]"
        />
      ) : null}
    </span>
  );
}
